/**
 * La session d'un élève avec le champion, vue du masteur (pur) : son historique et son bac « À décider ».
 *
 * Une suggestion ne se perd jamais (Wajih, 02/11) : sa naissance laisse une trace ; tant que sa condition
 * tient, elle est dans le bac, au-dessus du champ ; périmée ou décidée, elle en sort d'elle-même et laisse
 * une seconde trace qui dit pourquoi. Le bac n'est fait que des suggestions ouvertes : une proposition
 * périmée n'y a plus de bouton.
 *
 * Au retour sur un élève, le repère « Depuis ton dernier passage » se pose avant la première ligne plus
 * récente que le dernier passage — seulement s'il sépare quelque chose.
 */
import { countLabel } from "./textFormatter"
import { type UIActivityGenerationCatalogs, type UIActivityGenerationDraft } from "../types/activityGeneration"
import {
    type UITutorFeedItem,
    type UITutorFeedSignal,
    type UITutorSessionElsewhere,
    type UITutorSessionEntry,
    type UITutorSessionSetting,
    type UITutorSessionSettingField,
    type UITutorSuggestion,
} from "../types/tutorLive"
import { tutorFeedPriorityRank } from "./tutorFeedFormatter"

/**
 * Le rang dans le bac : ce que le champion a déjà fait et qui se défait encore (une relance en direct,
 * annulable) passe devant, puis la priorité de l'ancien panneau.
 */
function trayRank(suggestion: UITutorSuggestion): number {
    return suggestion.item.sam === "acts" ? 0 : tutorFeedPriorityRank(suggestion.item.priority)
}

/** La trace d'une suggestion qui a vécu sans décision : discrète, elle dit laquelle et où elle est passée. */
const expiredText = (item: UITutorFeedItem) => `Sans décision : « ${item.text} », rangée dans l'historique`

/** Deux instants ISO : leur ordre, minuit compris. */
function byTime(a: string, b: string): number {
    return Date.parse(a) - Date.parse(b)
}

/**
 * La durée de vie d'une suggestion, en minutes, par signal (Wajih, round 4 : « au bout d'un certain temps,
 * les suggestions ou décisions doivent disparaître et se mettre juste dans l'historique »). Elle s'ajoute à
 * la condition de validité : passé ce délai sans décision, la carte quitte le bac et devient une trace
 * discrète. `null` : elle vit tant que son signal est vrai — une main levée, un élève qui se tait ou qui est
 * sorti, un rendu à valider ne périment pas ; ils s'escaladent comme en prod (« Sans réponse »).
 */
const TUTOR_SUGGESTION_LIFETIME: Record<UITutorFeedSignal, number | null> = {
    hand_raised: null,
    silence: null,
    out_of_app: null,
    exercise_done: null,
    sam_ongoing: null,
    auto_activity: 5,
    sam_summary: 10,
    sam_proposal: 5,
    sam_whisper: 2,
    sam_nudge: 1,
}

/** L'instant (ISO) où une suggestion née à `at` quitte le bac d'elle-même ; absent : jamais. */
export function tutorSuggestionExpiresAt(signal: UITutorFeedSignal, at: string): string | undefined {
    const minutes = TUTOR_SUGGESTION_LIFETIME[signal]
    return minutes == null ? undefined : new Date(Date.parse(at) + minutes * 60_000).toISOString()
}

type Timed = { entry: UITutorSessionEntry; at: string; order: number }

export function tutorSessionTimeline(input: {
    entries: UITutorSessionEntry[]
    suggestions: UITutorSuggestion[]
    /** L'instant (ISO) du dernier passage du masteur sur cet élève. Absent : pas de repère. */
    lastSeenAt?: string
    /** L'instant (ISO) qu'il est : une suggestion ouverte dont la durée de vie est passée devient une trace discrète. */
    now?: string
}): { history: UITutorSessionEntry[]; tray: UITutorFeedItem[] } {
    const { entries, suggestions, lastSeenAt, now } = input
    // L'instant où une suggestion ouverte a quitté le bac ; absent : elle y est encore.
    const expiredAt = (suggestion: UITutorSuggestion) => {
        if (suggestion.state.kind !== "open" || now == null) return undefined
        const end = tutorSuggestionExpiresAt(suggestion.item.signal, suggestion.at)
        return end != null && byTime(end, now) <= 0 ? end : undefined
    }
    const timed: Timed[] = []
    for (const entry of entries) {
        if (entry.kind === "since") continue
        timed.push({ entry, at: entry.at, order: timed.length })
    }
    for (const suggestion of suggestions) {
        const { item, at, createdText, state } = suggestion
        timed.push({
            entry: { kind: "trace", id: `${item.id}-created`, at, text: createdText },
            at,
            order: timed.length,
        })
        if (state.kind !== "open") {
            timed.push({
                entry: { kind: "trace", id: `${item.id}-${state.kind}`, at: state.at, text: state.text },
                at: state.at,
                order: timed.length,
            })
        } else {
            const at = expiredAt(suggestion)
            if (at == null) continue
            timed.push({
                entry: { kind: "trace", id: `${item.id}-expired`, at, text: expiredText(item), quiet: true },
                at,
                order: timed.length,
            })
        }
    }
    timed.sort((a, b) => byTime(a.at, b.at) || a.order - b.order)
    const history = timed.map((t) => t.entry)

    if (lastSeenAt != null) {
        const first = timed.findIndex((t) => byTime(t.at, lastSeenAt) > 0)
        if (first > 0) history.splice(first, 0, { kind: "since", id: "since" })
    }

    const tray = suggestions
        .filter((suggestion) => suggestion.state.kind === "open" && expiredAt(suggestion) == null)
        .sort((a, b) => trayRank(a) - trayRank(b) || byTime(a.at, b.at))
        .map((suggestion) => suggestion.item)
    return { history, tray }
}

/**
 * Les réglages d'une génération lus dans le brouillon du formulaire d'aujourd'hui
 * (`UIActivityGenerationDraft`, aucune donnée nouvelle) : « 4ème · Maths · Fonctions affines · Entraînement
 * · 5 min ». Un réglage manquant garde sa place, à remplir (`missing`), avec le nom du champ.
 */
export function activityDraftSettings(
    draft: UIActivityGenerationDraft,
    catalogs: UIActivityGenerationCatalogs
): UITutorSessionSetting[] {
    const subject = catalogs.subjects.find((s) => s.id === draft.subjectId)
    const chapter = catalogs.chapters.find((c) => c.id === draft.chapterId)
    const level = catalogs.levels.find((l) => l.value === draft.level)
    const duration = catalogs.durations.find((d) => d.value === draft.duration)
    const setting = (
        field: UITutorSessionSettingField,
        value: string | undefined,
        empty: string,
        iconUri?: string
    ) => ({
        field,
        label: value ?? empty,
        missing: value == null,
        ...(iconUri != null && { iconUri }),
    })
    return [
        setting("class", draft.className ?? undefined, "Classe"),
        setting("subject", subject?.name, "Matière", subject?.iconUrl),
        setting("chapter", chapter?.name, "Chapitre"),
        setting("level", level?.label, "Type", level?.iconUri),
        setting("duration", duration?.label, "Durée"),
    ]
}

/**
 * Les réglages facultatifs de la génération, après ceux d'`activityDraftSettings` : les compétences (« 2
 * compétences » quand le masteur en a coché), et « Personnaliser » avec les centres d'intérêt de l'élève quand
 * il en a (une bascule). Jamais manquants : rien ne les exige.
 */
export function activityDraftOptions(
    draft: UIActivityGenerationDraft,
    options: { interests?: string | null }
): UITutorSessionSetting[] {
    const skills = draft.skillPartPositions.length
    return [
        {
            field: "skills",
            // Aucune cochée : le générateur choisit (la puce porte son nom au-dessus d'elle, W15).
            label: skills === 0 ? "Au choix" : countLabel(skills, "compétence"),
            missing: false,
        },
        ...(options.interests != null && options.interests.trim() !== ""
            ? [
                  {
                      field: "personalize" as const,
                      label: draft.personalizeWithInterests ? "Ses centres d'intérêt" : "Non",
                      missing: false,
                      on: draft.personalizeWithInterests,
                  },
              ]
            : []),
    ]
}

/** Ce qui attend le masteur chez un élève, dit pareil partout (vignette, bulle, « Ailleurs », cloche). */
export function tutorPendingLabel(count: number): string {
    return `${count} à décider`
}

/**
 * Les élèves qui attendent le masteur : ce qu'ils ont à décider, ou une urgence ; les urgences d'abord, puis
 * l'ordre de la pellicule. La colonne en retire l'élève regardé (« Ailleurs ») ; repliée, elle les montre tous.
 */
export function tutorSessionElsewhere(input: {
    students: { id: string; firstname: string; avatarUrl?: string; pending?: number }[]
    items: UITutorFeedItem[]
}): UITutorSessionElsewhere[] {
    const urgentByStudent = new Map<string, UITutorFeedItem>()
    for (const item of input.items) {
        if (item.priority === "urgent" && !urgentByStudent.has(item.student.id))
            urgentByStudent.set(item.student.id, item)
    }
    const rows: UITutorSessionElsewhere[] = []
    for (const student of input.students) {
        const urgent = urgentByStudent.get(student.id)
        const pending = student.pending ?? 0
        if (pending === 0 && urgent == null) continue
        rows.push({
            student: { id: student.id, firstname: student.firstname, avatarUrl: student.avatarUrl },
            pending,
            urgent,
        })
    }
    return rows.sort((a, b) => Number(b.urgent != null) - Number(a.urgent != null))
}
